import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { mapFactory } from 'common/utils/Map';
import { checkIsGoogleMap, checkIsHereMap } from 'entities/map';

import { MapContext as Context } from './types';

const MapContext = createContext({} as Context);
export const useMapContext = () => useContext(MapContext);

export const MapProvider = ({ children }: { children?: React.ReactNode }) => {
  const { locale } = useIntl();
  const [map, setMap] = useState<Context['map']>(null);

  const initMap: Context['initMap'] = useCallback((mapName, containerRef) => {
    if ((checkIsGoogleMap(mapName) && window.google) || checkIsHereMap(mapName)) {
      const newMap = mapFactory(mapName, containerRef.current);
      newMap.initMap({ lang: locale });
      window.map = newMap;
      setMap(newMap);
      return newMap;
    }
  }, [ setMap, locale ]);

  const setFitBounds: Context['setFitBounds'] = useCallback((value, padding) => {
    if (map) map.fitBounds(value, padding);
  }, [map]);

  const setCenter: Context['setCenter'] = useCallback((coords) => {
    if (map) {
      map.setCenter(coords);
    }
  }, [map]);

  const setZoom: Context['setZoom'] = useCallback((value) => {
    if (map) map.setZoom(value);
  }, [map]);

  const addListenerDragStart: Context['addListenerDragStart'] = useCallback((onDragStart) => {
    if (map) return map.addListenerDragStart(onDragStart);
  }, [map]);

  const addListenerBoundsChanged: Context['addListenerBoundsChanged'] = useCallback((onBoundsChanged) => {
    if (map) return map.addListenerBoundsChanged(onBoundsChanged);
  }, [map]);

  const addListenerStreetViewChange: Context['addListenerStreetViewChange'] = useCallback((onStreetViewChanged) => {
    if (map && onStreetViewChanged) return map.addListenerStreetViewChange(onStreetViewChanged);
  }, [map]);

  const addClickListener: Context['addClickListener'] = useCallback((listener) => {
    if (map) {
      map.addClickListener(listener);
    }
  }, [map]);

  const removeClickListener: Context['removeClickListener'] = useCallback((listener) => {
    if (map) {
      map.removeClickListener(listener);
    }
  }, [map]);

  const addZoomChangeEndListener: Context['addZoomChangeEndListener'] = useCallback((listener) => {
    if (map) {
      map.addZoomChangeEndListener(listener);
    }
  }, [map]);

  const removeZoomChangeEndListener: Context['removeZoomChangeEndListener'] = useCallback((listener) => {
    if (map) {
      map.removeZoomChangeEndListener(listener);
    }
  }, [map]);

  const setMapType: Context['setMapType'] = useCallback((mapType) => {
    if (map) map.setMapType(mapType);
  }, [map]);

  const value = useMemo(
    () => ({
      map,
      setMap,
      initMap,
      setFitBounds,
      setCenter,
      setZoom,
      addListenerDragStart,
      addListenerBoundsChanged,
      addListenerStreetViewChange,
      addClickListener,
      removeClickListener,
      addZoomChangeEndListener,
      removeZoomChangeEndListener,
      setMapType
    }),
    [
      map,
      setMap,
      initMap,
      setFitBounds,
      setCenter,
      setZoom,
      addListenerDragStart,
      addListenerBoundsChanged,
      addListenerStreetViewChange,
      addClickListener,
      removeClickListener,
      addZoomChangeEndListener,
      removeZoomChangeEndListener,
      setMapType
    ]
  );
  
  return (
    <MapContext.Provider value={value}>{children}</MapContext.Provider>
  );
};
